import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { AlertTriangle, Loader2, Sparkles, TrendingDown, TrendingUp } from "lucide-react";
import { api, inr, logError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const RANGES = [
  ["7", "7 days"],
  ["30", "30 days"],
  ["180", "6 months"],
  ["365", "1 year"],
];

const HEALTH = {
  out: { label: "Out of stock", colour: "#7f1d1d" },
  critical: { label: "Critical", colour: "#dc2626" },
  low: { label: "Low", colour: "#d97706" },
  healthy: { label: "Healthy", colour: "#15803d" },
};

const AXIS_TICK = { fontSize: 10 };
const GROUP_COLOURS = ["#0f3d2e", "#c9a227", "#1d4ed8", "#b45309", "#0e7490", "#7c3aed", "#64748b"];

export default function InventoryAnalytics() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const valuedGroups = useMemo(
    () => (data?.groups || []).filter((g) => g.value > 0),
    [data]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/inventory/analytics?days=${days}`);
      setData(d);
    } catch (e) {
      logError("inventory analytics", e);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !data) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const physical = Object.entries(data.totals.physical_by_unit || {});
  const healthData = data.health.filter((h) => h.count > 0);
  const movement = Object.entries(data.movement || {})
    .filter(([, v]) => v > 0)
    .map(([kind, qty]) => ({ kind: kind.replace(/_/g, " "), qty }));
  const drillItems = drill ? data.health_items[drill] || [] : [];
  const TrendIcon = data.direction === "decreasing" ? TrendingDown : TrendingUp;

  return (
    <div className="space-y-4" data-testid="inventory-analytics">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="overline">Inventory overview</div>
        <div className="flex gap-1" data-testid="analytics-range-buttons">
          {RANGES.map(([v, label]) => (
            <Button
              key={v}
              size="sm"
              variant={days === v ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              data-testid={`analytics-range-${v}`}
              onClick={() => setDays(v)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="analytics-cards">
        {[
          ["Total items", data.totals.items, "in stock register"],
          ["Healthy", data.totals.healthy, "above reorder level"],
          ["Low", data.totals.low, "at or below reorder"],
          ["Critical", data.totals.critical, "half of reorder or less"],
          ["Out of stock", data.totals.out_of_stock, "nothing left"],
        ].map(([label, value, hint]) => (
          <div key={label} className="surface p-4">
            <div className="overline">{label}</div>
            <div className="mt-1 font-display text-2xl font-semibold">{value}</div>
            <div className="text-[11px] text-muted-foreground">{hint}</div>
          </div>
        ))}
      </div>

      <div className="surface p-4" data-testid="analytics-physical-stock">
        <div className="overline mb-2">Total physical stock</div>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          {physical.length === 0 ? (
            <span className="text-muted-foreground">No stock recorded.</span>
          ) : (
            physical.map(([unit, qty]) => (
              <span key={unit} className="font-mono-num">
                {qty.toLocaleString("en-IN")} <span className="text-muted-foreground">{unit}</span>
              </span>
            ))
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="surface p-4">
          <div className="overline mb-2">Stock health (tap a slice)</div>
          <ResponsiveContainer width="100%" height={170} minHeight={170}>
            <PieChart>
              <Pie
                data={healthData}
                dataKey="count"
                nameKey="status"
                innerRadius={38}
                outerRadius={64}
                onClick={(e) => setDrill(e?.payload?.status || null)}
              >
                {healthData.map((h) => (
                  <Cell key={h.status} fill={HEALTH[h.status].colour} cursor="pointer" />
                ))}
              </Pie>
              <Tooltip formatter={(v, n) => [`${v} items`, HEALTH[n]?.label || n]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-3 text-xs">
            {healthData.map((h) => (
              <button
                key={h.status}
                type="button"
                data-testid={`health-legend-${h.status}`}
                className="flex items-center gap-1 hover:underline"
                onClick={() => setDrill(h.status)}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: HEALTH[h.status].colour }} />
                {HEALTH[h.status].label} ({h.count})
              </button>
            ))}
          </div>
          {drill && (
            <div className="mt-3 border-t border-border pt-2" data-testid="health-drilldown">
              <div className="mb-1 flex items-center justify-between text-xs">
                <span className="font-medium">{HEALTH[drill].label} items</span>
                <button type="button" className="text-muted-foreground hover:underline" onClick={() => setDrill(null)}>
                  close
                </button>
              </div>
              {drillItems.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nothing in this group.</p>
              ) : (
                drillItems.map((i) => (
                  <div key={i.id} className="flex justify-between py-0.5 text-xs">
                    <span>{i.name}</span>
                    <span className="font-mono-num">
                      {i.primary}
                      {i.secondary ? ` • ${i.secondary}` : ""}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="surface p-4">
          <div className="overline mb-2">Items by material group</div>
          <ResponsiveContainer width="100%" height={170} minHeight={170}>
            <BarChart data={data.groups} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="group" tick={AXIS_TICK} interval={0} angle={-15} height={40} />
              <YAxis tick={AXIS_TICK} allowDecimals={false} />
              <Tooltip formatter={(v) => [`${v} items`, "Items"]} />
              <Bar dataKey="items" radius={[3, 3, 0, 0]}>
                {data.groups.map((g, i) => (
                  <Cell key={g.group} fill={GROUP_COLOURS[i % GROUP_COLOURS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="text-[11px] text-muted-foreground">
            Counted as items — quantities in different units are never added together.
          </p>
        </div>

        <div className="surface p-4">
          <div className="overline mb-2 flex items-center gap-1.5">
            Stock movement ({days} days)
            <Badge variant="outline" className="gap-1 text-[10px]">
              <TrendIcon className="h-3 w-3" /> {data.direction}
            </Badge>
          </div>
          {movement.length === 0 ? (
            <p className="text-xs text-muted-foreground">No movement in this period.</p>
          ) : (
            <ResponsiveContainer width="100%" height={150} minHeight={150}>
              <BarChart data={movement} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="kind" tick={AXIS_TICK} interval={0} />
                <YAxis tick={AXIS_TICK} />
                <Tooltip />
                <Bar dataKey="qty" fill="#0f3d2e" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="surface p-4">
          <div className="overline mb-2">Net stock trend</div>
          {(data.trend || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">Not enough movement to plot a trend yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={150} minHeight={150}>
              <LineChart data={data.trend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} />
                <Tooltip />
                <Line type="monotone" dataKey="net" stroke="#c9a227" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="surface p-4" data-testid="fastest-decreasing">
          <div className="overline mb-2">Fastest decreasing stock</div>
          {(data.fastest_decreasing || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No consumption recorded in this period.</p>
          ) : (
            data.fastest_decreasing.map((f) => (
              <div key={f.id} className="flex items-center justify-between border-b border-border py-1.5 text-xs last:border-0">
                <span>{f.name}</span>
                <span className="flex items-center gap-2">
                  <span className="font-mono-num text-muted-foreground">
                    {f.consumed} {f.unit} used
                    {f.days_left != null ? ` • ~${f.days_left}d left` : ""}
                  </span>
                  <Badge
                    variant={f.speed === "High" ? "destructive" : f.speed === "Medium" ? "secondary" : "outline"}
                    className="text-[9px]"
                  >
                    {f.speed}
                  </Badge>
                </span>
              </div>
            ))
          )}
        </div>

        <div className="surface p-4" data-testid="low-stock-alerts">
          <div className="overline mb-2 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 text-destructive" /> Low stock alerts
          </div>
          {(data.alerts || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">Everything is above its reorder level.</p>
          ) : (
            data.alerts.map((a) => (
              <div key={a.id} className="flex items-center justify-between border-b border-border py-1.5 text-xs last:border-0">
                <span>
                  {a.name}
                  <span className="ml-2 text-muted-foreground">reorder at {a.reorder_level}</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="font-mono-num">{a.primary}</span>
                  <Badge
                    variant={a.status === "healthy" ? "outline" : "destructive"}
                    className="text-[9px] uppercase"
                  >
                    {a.status === "out" ? "out of stock" : a.status}
                  </Badge>
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {data.can_see_cost && (
        <div className="surface p-4" data-testid="analytics-financial">
          <div className="overline mb-2">Inventory value (Owner view)</div>
          <div className="font-display text-2xl font-semibold" data-testid="analytics-total-value">
            {inr(data.total_value)}
          </div>
          <div className="mt-2 grid gap-1 sm:grid-cols-2">
            {valuedGroups.map((g) => (
              <div key={g.group} className="flex justify-between text-xs">
                <span className="text-muted-foreground">{g.group}</span>
                <span className="font-mono-num">{inr(g.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="surface p-4" data-testid="inventory-insight">
        <div className="overline mb-2 flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-primary" /> Inventory insight
        </div>
        {(data.insights || []).map((line) => (
          <p key={line} className="py-0.5 text-sm">
            {line}
          </p>
        ))}
      </div>
    </div>
  );
}
